import { useState, useEffect, useRef } from "react";
import Pubnub from "pubnub";
import { getNotificationHistory } from "../redux/slices/notificationSlice";
import { useSelector, useDispatch } from "react-redux";
import store from "../redux/store";
import badgeManager from "../utility/badgeManager";
import { PushNotifications } from '@capacitor/push-notifications';

const usePubNub = (identity) => {
  const userIdentity = identity || "test"; // Default identity to "test" if not provided
  const [messages, setMessages] = useState([]);
  const pubnub = useSelector((state) => state.user.pubnub);
  const user = useSelector((state) => state.auth.user); // Watch for user authentication changes
  const dispatch = useDispatch();
  const pubnubRef = useRef(null);
  const subscribedChannelsRef = useRef([]);
  const processedMessagesRef = useRef(new Set()); // Track processed messages to prevent duplicates
  const fcmTokenRef = useRef(null);
  const pushNotificationsInitializedRef = useRef(false);
  const previousUserIdentityRef = useRef(null);
  
  console.log('🔧 usePubNub hook called with identity:', userIdentity);
  console.log('🔧 Current pubnub from store:', pubnub);
  console.log('🔧 Current user state:', store.getState().auth);
  console.log('🔧 User identity for channel:', userIdentity);
  console.log('🔧 Channel name will be:', `user_${userIdentity}`);
  
  const handleMessage = async (message) => {
    console.log("📨 Received message:", message);
    console.log("📨 Message structure:", JSON.stringify(message, null, 2));
    
    // Create a unique message identifier to prevent duplicates
    const messageId = `${message.publisher}_${message.timetoken}_${message.channel}`;
    if (processedMessagesRef.current.has(messageId)) {
      console.log("📨 Message already processed, skipping:", messageId);
      return;
    }
    processedMessagesRef.current.add(messageId);
    
    // Clean up old message IDs (keep last 100)
    if (processedMessagesRef.current.size > 100) {
      const ids = Array.from(processedMessagesRef.current);
      processedMessagesRef.current = new Set(ids.slice(-50));
    }
    
    // Extract message content
    let messageText = 'You have a new message';
    let senderName = 'Someone';
    
    if (message.message) {
      if (typeof message.message === 'string') {
        messageText = message.message;
      } else if (message.message.text) {
        messageText = message.message.text;
      } else if (message.message.description) {
        messageText = message.message.description;
      } else if (message.message.body) {
        messageText = message.message.body;
      }
      
      senderName = message.message.senderName || message.message.sender_name || 'Someone';
    }
    
    console.log("📨 Extracted message info:", { messageText, senderName });
    
    // Check if app is in background
    const isAppInBackground = document.hidden;
    console.log("📱 App in background:", isAppInBackground);
    
    // For PubNub push notifications, we don't need to show local notifications
    // FCM will handle the display when app is closed/backgrounded
    if (isAppInBackground) {
      console.log("📱 App is in background, FCM will handle notification display");
      
      // Increment badge count for new message
      await badgeManager.incrementBadge();
    }
    
    // Always update messages and notification history
    setMessages((prevMessages) => [message, ...prevMessages]);
    dispatch(getNotificationHistory());
  };

  // Function to clean up PubNub instance
  const cleanupPubNub = () => {
    if (pubnubRef.current) {
      console.log('🔧 Cleaning up PubNub instance for user logout');
      try {
        // Unsubscribe from all channels
        pubnubRef.current.unsubscribeAll();
        
        // Remove all listeners
        pubnubRef.current.removeAllListeners();
        
        // Clear the reference
        pubnubRef.current = null;
        
        // Clear subscribed channels
        subscribedChannelsRef.current = [];
        
        // Clear processed messages
        processedMessagesRef.current.clear();
        
        console.log('✅ PubNub cleanup completed');
      } catch (error) {
        console.log('❌ Error during PubNub cleanup:', error);
      }
    }
  };

  // Function to test PubNub subscription by sending a test message
  const testPubNubSubscription = async () => {
    if (!pubnubRef.current || !userIdentity || userIdentity === "test") {
      console.log('❌ Cannot test subscription - missing PubNub instance or valid user identity');
      return;
    }

    try {
      console.log('🧪 Testing PubNub subscription by sending test message...');
      const testMessage = {
        text: 'Test message from PubNub hook',
        timestamp: Date.now(),
        test: true
      };
      
      await pubnubRef.current.publish({
        channel: `user_${userIdentity}`,
        message: testMessage
      });
      
      console.log('✅ Test message sent successfully');
    } catch (error) {
      console.log('❌ Failed to send test message:', error);
    }
  };



  // Function to handle FCM token from Capacitor
  const handleFCMTokenRegistration = async (fcmToken) => {
    if (!fcmToken) {
      console.log('❌ Cannot handle FCM token - missing FCM token');
      return;
    }

    try {
      console.log('🔔 FCM token received from Capacitor:', fcmToken);
      console.log('✅ FCM token is ready for push notifications');
      
      // Store the FCM token for potential future use
      fcmTokenRef.current = fcmToken;
      
      // Capacitor handles FCM registration automatically
      // Firebase Admin SDK will send push notifications via FCM
      console.log('📱 Push notifications handled by Firebase Admin SDK');
    } catch (error) {
      console.log('❌ Error handling FCM token:', error);
    }
  };

  // Handle user authentication changes (login/logout)
  useEffect(() => {
    const currentUserIdentity = user?.identity;
    
    console.log('🔧 User authentication state changed:', {
      previous: previousUserIdentityRef.current,
      current: currentUserIdentity,
      userIdentity: userIdentity
    });
    
    // If user logged out (had identity before, now doesn't)
    if (previousUserIdentityRef.current && !currentUserIdentity) {
      console.log('🔧 User logged out, cleaning up PubNub');
      cleanupPubNub();
    }
    
    // If user logged in (didn't have identity before, now does)
    if (!previousUserIdentityRef.current && currentUserIdentity) {
      console.log('🔧 User logged in, forcing PubNub reinitialization');
      // Force reinitialization by triggering the main useEffect
      // This ensures PubNub is set up immediately after login
    }
    
    // If user identity changed (different user logged in)
    if (previousUserIdentityRef.current && currentUserIdentity && previousUserIdentityRef.current !== currentUserIdentity) {
      console.log('🔧 Different user logged in, cleaning up and reinitializing PubNub');
      cleanupPubNub();
      // The main useEffect will handle reinitializing with the new identity
    }
    
    // Update previous identity reference
    previousUserIdentityRef.current = currentUserIdentity;
  }, [user?.identity]);

  useEffect(() => {
    console.log('🔧 Setting up PubNub with identity:', userIdentity);
    
    // Set up push notifications immediately if not already done
    const setupPushNotifications = async () => {
      console.log('🔔 Setting up push notifications...');
      console.log('🔔 PushNotifications plugin check:', {
        capacitor: !!window.Capacitor,
        plugins: !!(window.Capacitor && window.Capacitor.Plugins),
        pushNotifications: !!(window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.PushNotifications)
      });
      
      // Always try to set up push notifications (don't skip)
      pushNotificationsInitializedRef.current = true;
      try {
        console.log('🔔 Setting up push notifications...');
        
        // Check if PushNotifications plugin is available
        if (!window.Capacitor || !window.Capacitor.Plugins || !window.Capacitor.Plugins.PushNotifications) {
          console.log('❌ PushNotifications plugin not available, skipping setup');
          console.log('🔧 Capacitor available:', !!window.Capacitor);
          console.log('🔧 Capacitor.Plugins available:', !!(window.Capacitor && window.Capacitor.Plugins));
          console.log('🔧 PushNotifications plugin available:', !!(window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.PushNotifications));
          return;
        }
        
        // Request permission
        console.log('🔔 Requesting push notification permissions...');
        const permission = await PushNotifications.requestPermissions();
        console.log('🔔 Push notification permission result:', permission);
        
        if (permission.receive === 'granted') {
          // Register for push notifications
          console.log('🔔 Registering for push notifications...');
          await PushNotifications.register();
          console.log('✅ Push notifications registration initiated');
          
          // Listen for registration
          PushNotifications.addListener('registration', (token) => {
            console.log('🔔 Push registration success, token:', token.value);
            
            // Handle FCM token registration
            handleFCMTokenRegistration(token.value);
          });
          
          // Listen for registration errors
          PushNotifications.addListener('registrationError', (error) => {
            console.log('❌ Push registration failed:', error);
          });
          
          // Listen for push notifications received when app is in foreground
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('🔔 Push notification received (foreground):', notification);
            
            // Handle the notification data
            const notificationData = notification.data;
            if (notificationData && notificationData.messageType === 'chat') {
              console.log('🔔 Chat push notification received:', notificationData);
              
              // Extract chat info and navigate
              const { senderId, senderName, channelId } = notificationData;
              
              // Store chat info for navigation
              localStorage.setItem('pendingChatInfo', JSON.stringify({
                senderId,
                senderName,
                channelId
              }));
              
              // Navigate to chat if not already there
              setTimeout(() => {
                if (window.location.pathname !== '/chat') {
                  window.location.href = '/chat';
                }
              }, 100);
            }
          });
          
          // Listen for push notification actions (when user taps notification)
          PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            console.log('🔔 Push notification action performed:', notification);
            
            const notificationData = notification.notification.data;
            if (notificationData && notificationData.messageType === 'chat') {
              console.log('🔔 Chat push notification action performed:', notificationData);
              
              // Extract chat info and navigate
              const { senderId, senderName, channelId } = notificationData;
              
              // Store chat info for navigation
              localStorage.setItem('pendingChatInfo', JSON.stringify({
                senderId,
                senderName,
                channelId
              }));
              
              // Navigate to chat
              setTimeout(() => {
                if (window.location.pathname !== '/chat') {
                  window.location.href = '/chat';
                }
              }, 100);
            }
          });
        } else {
          console.log('❌ Push notification permission denied');
        }
      } catch (error) {
        console.log('❌ Failed to set up push notifications:', error);
      }
    };
    
    // Set up push notifications immediately (regardless of user login status)
    console.log('🔔 About to call setupPushNotifications...');
    setupPushNotifications().catch(error => {
      console.log('❌ setupPushNotifications failed with error:', error);
    });
    
    const setupPubNub = async () => {
      // Only create PubNub instance if we have a valid identity (user is logged in)
      if (userIdentity && userIdentity !== "test") {
        if (!pubnub) {
          console.log('🔧 Creating new PubNub instance for user:', userIdentity);
          pubnubRef.current = new Pubnub({
            publishKey: process.env.REACT_APP_PUBNUB_PUBLISH,
            subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE,
            userId: userIdentity,
          });
          console.log('🔧 PubNub instance created:', pubnubRef.current);
        } else {
          console.log('🔧 Using existing PubNub instance from store');
          pubnubRef.current = pubnub;
        }
        
        // Push notifications are handled by Firebase Admin SDK
        console.log('🔔 Push notifications handled by Firebase Admin SDK');
        
        console.log('🔧 Adding message listener');
        pubnubRef.current.addListener({
          message: (msg) => {
            console.log('🔔 PubNub message event:', msg);
            handleMessage(msg);
          },
          status: (statusEvent) => {
            console.log('🔔 PubNub status event:', statusEvent);
            
            // Handle connection status changes
            if (statusEvent.category === 'PNConnectedCategory') {
              console.log('✅ PubNub connected successfully');
              
              // FCM token is already handled by Capacitor
              console.log('🔔 FCM token handling is managed by Capacitor');
            } else if (statusEvent.category === 'PNReconnectedCategory') {
              console.log('🔄 PubNub reconnected');
            } else if (statusEvent.category === 'PNDisconnectedCategory') {
              console.log('❌ PubNub disconnected');
            } else if (statusEvent.category === 'PNNetworkDownCategory') {
              console.log('🌐 PubNub network down');
            } else if (statusEvent.category === 'PNNetworkUpCategory') {
              console.log('🌐 PubNub network up');
            }
            
            // Log any errors
            if (statusEvent.error) {
              console.log('❌ PubNub error:', statusEvent.error);
            }
          },
          presence: (presenceEvent) => {
            console.log('🔔 PubNub presence event:', presenceEvent);
          }
        });
        
        // Check if the channel is already subscribed
        const channelName = `user_${userIdentity}`;
        if (!subscribedChannelsRef.current.includes(channelName)) {
          console.log('🔧 Subscribing to channels:', ["public", channelName]);
          
          try {
            await pubnubRef.current.subscribe({
              channels: ["public", channelName],
              withPresence: true,
            });
            subscribedChannelsRef.current.push(channelName);
            console.log('✅ Successfully subscribed to channels:', ["public", channelName]);
            
            // Push notifications are handled by backend PubNub service via FCM
            console.log('🔔 Push notifications handled by backend PubNub service');
            
            // Test the subscription after a short delay
            setTimeout(() => {
              testPubNubSubscription();
            }, 1000);
          } catch (error) {
            console.log('❌ Failed to subscribe to channels:', error);
          }
        } else {
          console.log('🔧 Already subscribed to channel:', channelName);
          
          // FCM token is already handled by Capacitor
          console.log('🔔 FCM token handling is managed by Capacitor');
        }
        
        // Set up heartbeat to keep connection alive
        const heartbeatInterval = setInterval(() => {
          if (pubnubRef.current) {
            console.log('💓 PubNub heartbeat - checking connection...');
            // Send a ping to keep connection alive
            pubnubRef.current.time().then((response) => {
              console.log('💓 PubNub heartbeat - connection alive, server time:', response.timetoken);
            }).catch((error) => {
              console.log('❌ PubNub heartbeat failed:', error);
            });
          }
        }, 30000); // Every 30 seconds
        
        // Clean up heartbeat on unmount
        return () => {
          clearInterval(heartbeatInterval);
        };
      } else {
        console.log('🔧 No valid user identity, skipping PubNub setup for now');
      }
    };

    setupPubNub();

    return () => {
      console.log('🔧 Cleaning up PubNub listener on unmount');
      cleanupPubNub();
    };
  }, [userIdentity, pubnub, dispatch]);

  return pubnubRef.current; // Return the current PubNub instance
};

export default usePubNub;